/**
 *
 * Dynamic Table
 *
 */
import StyleComponentProps from 'core/utils/types/StyleComponentProps';
import classNames from 'classnames';
import React, { memo, ReactNode, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components/macro';
import {
  PaginationState,
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  getSortedRowModel,
} from '@tanstack/react-table';
import { Button } from '../Button';
import { ReactComponent as LeftArrow } from 'assets/icons/LeftArrow.svg';
import { ReactComponent as RightArrow } from 'assets/icons/RightArrow.svg';
import { ReactComponent as DownArrowBlack } from 'assets/icons/DownArrowBlack.svg'
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { paginationOptions } from 'core/utils/constants/DateTime';
import NoDataFound from '../NoDataFound';
import { SetupLoader } from '../SetupComponents/SetupLoader';
import Modal from 'react-modal';
import { ReactComponent as Close } from 'assets/icons/Close.svg';
import { DropDown } from 'libs/DropDown';
import { error, success } from '../Notify';
import httpclient from 'libs/Axios';
import { useLocation, useParams } from 'react-router-dom';
import { useAuthenticationSlice } from 'features/Authentication/slice';
import { useDispatch } from 'react-redux';
import { checkAdmin, reorderTable } from 'core/utils/constants';
import debounce from 'lodash/debounce';
import { ExportButton } from '../ExportButton/ExportButton';
import throttle from 'lodash/throttle';
import { showGlobalModal } from '../GlobalModal';
import EncryptedStorage from 'core/utils/constants/encryptedStorage';
import StatusColorIndicator from '../StatusColorIndicator';

export const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '30rem',
    borderRadius: '1.5em',
    boxShadow: '0px 16px 32px 4px rgba(0, 0, 0, 0.15)',
    padding: '1.3em',
    zIndex: "2000"
  },
  overlay: {
    background: 'rgba(0, 0, 0, 0.4)',
    zIndex: "2000"
  },
};

interface Props extends StyleComponentProps {
  columns: any[];
  data: any[];
  onRowClick?: any;
  hoverTransparent?: boolean;
  pageIndex?: number;
  pageSize?: number;
  defaultColumns?: {
    minSize: number;
    maxSize: number;
  };
  setPagination?: React.Dispatch<React.SetStateAction<PaginationState>>;
  notWantPages?: boolean;
  previousYearValidationNotRequired?: boolean;
  totalEntries: number;
  manualShowingFrom?: number;
  manualShowingTo?: number;
  dispatch?: any;
  route?: string;
  selectedClass?: number;
  columnsSelectionDispatch?: any;
  defaultColumnsForSelectection?: any;
  customData?: any;
  headerControls?: () => ReactNode;
  headerControlsLeft?: () => ReactNode;
  className?: string;
  examName?: string;
  contentHeight?: number;
  handleFeedbackCheckboxToggle?: any;
}

export function fetchData(
  data: any[],
  options: {
    pageIndex: number;
    pageSize: number;
  },
  totalEntries?: number,
  isDynamicTable?: boolean
) {
  return {
    rows: isDynamicTable ? data?.slice(0, options.pageSize) : data?.slice(
      options.pageIndex * options.pageSize,
      (options.pageIndex + 1) * options.pageSize,
    ),
    pageCount: isDynamicTable ? Math.ceil((totalEntries || data?.length) / options.pageSize) : Math.ceil(data?.length / options.pageSize),
  };
}

const MemoizedTableRow = memo(({ row, onRowClick, hoverTransparent, getRowPadding }: any) => {
  const status = row?.original?.DisplayStudentClassStatus?.toUpperCase();
  const trClass = classNames(
    'cursor-pointer',
    !hoverTransparent && 'hover:bg-neutral-30',
    {
      'bg-yellow-300': status === 'INACTIVE',
      'line-through text-gray-400': status === 'DELETED'
    }
  );

  return (
    <tr
      className={trClass}
      onClick={() => onRowClick?.(row)}
    >
      {row.getVisibleCells().map((cell: any) => (
      <MemoizedTableCell
        key={cell.id}
        cell={cell}
        getRowPadding={getRowPadding}
      />
    ))}
  </tr>
  );
});

const MemoizedTableCell = memo(({ cell, getRowPadding }: any) => {
  const cellValue = cell.getValue();
  const titleText = typeof cellValue === 'string' || typeof cellValue === 'number'
    ? cellValue.toString()
    : '';

  return (
    <td
      className={`!max-w-[40vw] min-w-[7vw] !pl-4 ${cell.column.columnDef['className']}`}
      style={{ padding: getRowPadding() }}
    >
      <div
        className="text-left truncate w-[90%]"
        title={titleText}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </div>
    </td>
  );
});

const MemoizedTableHeaderCell = memo(({ header }: any) => (
  <th
    className={`sticky top-0 bg-indigo-50 ${header.column.columnDef['className'] ?? ''}`}
    style={{ minWidth: header.column.columnDef.minSize, zIndex: 0 }}
  >
    <div onClick={header.column.getToggleSortingHandler()} className="cursor-pointer !min-w-max">
      {flexRender(header.column.columnDef.header, header.getContext())}
      {{ asc: "🠁", desc: "🠃" }[header.column.getIsSorted() as string] ?? null}
    </div>
  </th>
));

export const NewDynamicTable = memo((props: Props) => {

  const constantColumns = props?.defaultColumnsForSelectection?.map(item => ({
    ...item,
    ...(!item?.id && { id: item?.accessorKey || item?.header })
  }));

  const location = useLocation()

  const headerRef: any = React.useRef(null);
  const tableRef = React.useRef(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const params = useParams();
  const authData = useAuth();
  const dispatch = useDispatch();
  const { actions } = useAuthenticationSlice();
  const [{ pageIndex, pageSize }, setPagination] = React.useState<PaginationState>({ pageIndex: props?.pageIndex || 0, pageSize: props?.pageSize || paginationOptions.at(0) as number });
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [searchInput, setSearchInput] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [formLoading, setFormLoading] = React.useState(false);
  const [showModal, setshowModal] = React.useState({ display: false, ID: undefined });
  const [selectedColumns, setSelectedColumns] = React.useState<any>([]);
  const [selectedSavedColumns, setselectedSavedColumns] = React.useState<any>(authData?.COLUMNS?.find(item => item?.MODULE_NAME === params?.route) || {});
  const [debouncedSearchInput, setDebouncedSearchInput] = React.useState('');
  const [isSearching, setIsSearching] = React.useState(false);
  const [availableHeight, setAvailableHeight] = useState<string>("70vh"); // Default height

  const isCheckedList = params?.route === "staff" ? props?.data?.filter(({ IS_CHECKED }) => !!IS_CHECKED) : [];

  const debounceSearch = useMemo(
    () =>
      debounce((value) => {
        setDebouncedSearchInput(value);
      }, 300),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debounceSearch(value);
  };

  const getRowPadding = () => {
    if (props?.route && ['staff']?.includes(props?.route)) {
      return `0.6rem 0rem`
    } else if (props?.route && ['staffLeave']?.includes(props?.route)) {
      return `0.4rem 0rem`
    } else {
      return `0.9rem 0rem`
    }
  }

  const filterRow = (row, query) => {
    if (typeof row === 'object' && row !== null) {
      return Object.values(row)?.some(value => filterRow(value, query));
    }
    return row && row.toString().toLowerCase().includes(query.toLowerCase());
  };

  // Filtered data for search
  const filteredData = useMemo(() => {
    if (!debouncedSearchInput || debouncedSearchInput.length < 2) {
      setIsSearching(false);
      return props.data;
    }
    setIsSearching(true);
    return props.data?.filter((row) => filterRow(row, debouncedSearchInput));
  }, [debouncedSearchInput, props.data]);

  const fetchDataOptions = {
    pageIndex: pageIndex,
    pageSize: pageSize,
  };

  const dataQuery = fetchData(filteredData, fetchDataOptions, props?.totalEntries, !!props?.dispatch);

  const fetchListData = async () => {
    try {
      setFormLoading(formLoading => true);
      const response = await httpclient().get(`/master/tableColumnDisplay/list?institute_id=${authData?.INSTITUTE_ID}&branch_id=${authData?.BRANCH_ID}`)
      const data = response?.data?.data?.TableColumnDisplayList?.find(item => item?.MODULE_NAME === params?.route) || {};
      if (data?.DISPLAY_COLUMNS?.length) {
        setselectedSavedColumns(data);
        dispatch(actions.updateAuthData({
          COLUMNS: response?.data?.data?.TableColumnDisplayList
        }))
      }
      setFormLoading(formLoading => false);
    } catch (error) {
      console.error(error);
      setFormLoading(formLoading => false);
    }
  }

  const handleSelectedColumnSelection = async (cC?) => {
    try {
      if ((cC || selectedColumns)?.length) {
        setFormLoading(formLoading => true);
        const response = await httpclient().post(`/master/tableColumnDisplay/upsert`, [
          {
            TABLE_COLUMN_DISPLAY_ID: selectedSavedColumns?.TABLE_COLUMN_DISPLAY_ID,
            INSTITUTE_ID: authData?.INSTITUTE_ID,
            BRANCH_ID: authData?.BRANCH_ID,
            MODULE_NAME: params?.route,
            DISPLAY_COLUMNS: cC || selectedColumns
          }
        ]);
        if (!!response) {
          fetchListData();
        } else {
          setFormLoading(formLoading => false);
        }
      } else {
        error("Please Select One Column at least!", true);
      }
    } catch (err) {
      setFormLoading(formLoading => false);
      console.log(err)
    }
  }

  React.useEffect(() => {
    const handleScroll = () => {
      if (!!headerRef) {
        const table = headerRef?.current?.closest('div[style*="overflow-y: auto"]');
        if (table) {
          const scrollTop = table?.scrollTop;
          headerRef.current.style.transform = `translateY(${scrollTop}px)`;
        }
      }
    };

    const container = headerRef?.current?.closest('div[style*="overflow-y: auto"]');
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const updateHeight = React.useCallback(throttle(() => {
    if (containerRef.current) {
      const tableTop = containerRef.current.getBoundingClientRect().top;
      const newHeight = `calc(93vh - ${tableTop}px)`;
      if (tableTop < 400) setAvailableHeight(newHeight);
    }
  }, 100), []);

  useEffect(() => {
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [updateHeight]);

  React.useMemo(() => {
    setLoading(loading => false);
  }, [props.data]);

React.useEffect(() => {
  if (!!props?.dispatch) {
    setPagination({
      pageIndex: (props?.pageIndex || 0),
      pageSize: (props?.pageSize || 0)
    });
  }
}, [props?.pageIndex, props?.pageSize]); // Triggers when Redux state changes

  const pagination = React.useMemo(
    () => ({
      pageIndex: pageIndex,
      pageSize: pageSize,
    }),
    [pageIndex, pageSize],
  );

  const table = useReactTable({
    data: dataQuery.rows ?? [],
    pageCount: dataQuery?.pageCount ?? -1,
    columns: props.columns?.filter(item => {
      if (!props?.previousYearValidationNotRequired) {
        if (
          authData?.SELECTED_ACADEMIC_YEAR?.ACADEMIC_YEAR_STATUS !==
          'PREVIOUS' ||
          item?.id !== 'ACTION_HEADER'
        ) {
          return item;
        }
      } else {
        return item;
      }
    }),
    defaultColumn:
      props.defaultColumns &&
      (props.defaultColumns as Partial<ColumnDef<any, unknown>> | undefined),
    state: {
      pagination,
      sorting
    },
    onPaginationChange: ({ pageIndex, pageSize }: any) => {
      setPagination(() => ({ pageIndex, pageSize }));
      if (!!props?.dispatch) {
        props?.dispatch({
          page_index: pageIndex,
          page_size: +pageSize
        })
      }
      // fetchData(pageIndex, pageSize); // Call fetchData when pagination changes
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    // getPaginationRowModel: getPaginationRowModel(), // If only doing manual pagination, you don't need this
    onSortingChange: setSorting,
    // debugTable: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
  });

  React.useEffect(() => {
    EncryptedStorage.setItem('PAGINATION', JSON.stringify({ pageIndex, pageSize }));
  }, [pageIndex, pageSize]);

  const MemoizedTableBody = useMemo(() => () => (
    <tbody>
      {table.getRowModel().rows.map((row: any) => (
        <MemoizedTableRow
          key={row.id}
          row={row}
          onRowClick={props.onRowClick}
          hoverTransparent={props.hoverTransparent}
          getRowPadding={getRowPadding}
        />
      ))}
    </tbody>
  ), [table.getRowModel().rows, props.onRowClick, props.hoverTransparent]);

  const MemoizedTableHeader = useMemo(() => () => (
    <thead>
      {table.getHeaderGroups().map((headerGroup: any) => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header: any) => (
            <MemoizedTableHeaderCell
              key={header.id}
              header={header}
            />
          ))}
        </tr>
      ))}
    </thead>
  ), [table.getHeaderGroups()]);

  function formatString(input) {
    if (!input)
      return "";

    let result = '';

    const charactersToRemove = new Set([',', '₹', '$', '€', '£', '¥']);
    const removeNewLine = new Set(['\n']);

    for (let char of input) {
      if (charactersToRemove.has(char)) {
        result += "";
      } else if (removeNewLine.has(char)) {
        result += " | ";
      } else {
        result += char;
      }
    }

    return result?.trim();
  }





  const downloadCSV = (csv, filename) => {
    const csvFile = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const downloadLink = document.createElement('a');
    downloadLink.download = filename;
    downloadLink.href = window.URL.createObjectURL(csvFile);
    downloadLink.style.display = 'none';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const exportTableToCSV = (filename) => {

    if (params?.route === "examStudentResult") {
      const XLSX = require("xlsx-js-style");

      let ws_data: any[] = [];

      // Build Headers
      let headersRow = ["Student Name", "Class", "Roll No"];
      props?.customData?.ExamDetails?.Subjects?.forEach((item: any) => {
        headersRow.push(`${item?.Subject?.SUBJECT_NAME} (${item?.TOTAL_MARKS})`);
      });
      let totalMaxMarks = props?.customData?.ExamDetails?.Subjects?.reduce((sum: any, current: any) => sum + +(current?.TOTAL_MARKS || 0), 0);
      headersRow.push(`Total Marks (${totalMaxMarks})`);

      ws_data.push(headersRow);

      // Build Data Rows
      props?.customData?.StudentResults?.forEach((item: any) => {
        let rowArray: any[] = [];
        rowArray.push(`${item?.FIRST_NAME || ""} ${item?.LAST_NAME || ""}`);
        rowArray.push(`${props?.customData?.ExamDetails?.Class?.NAME || ""}`);
        rowArray.push(`${item?.StudentClass?.ROLL_NUMBER || ""}`);

        props?.customData?.ExamDetails?.Subjects?.forEach((subject: any) => {
          let result = item?.Results?.find((r: any) => r?.EXAM_SUBJECT_ID === subject?.EXAM_SUBJECT_ID);
          let subjectMark = result?.MARKS_OBTAINED !== undefined && result?.MARKS_OBTAINED !== null ? result?.MARKS_OBTAINED : "-";

          let paramMarks = subject?.ParameterResults?.map((param: any) => {
            let paramResult = result?.ParameterResults?.find((pr: any) => pr?.PARAMETER_ID === param?.PARAMETER_ID);
            let paramMarkValue = paramResult?.PARAMETER_OBTAINED_MARKS !== undefined && paramResult?.PARAMETER_OBTAINED_MARKS !== null ? paramResult?.PARAMETER_OBTAINED_MARKS : "-";
            return `${param?.PARAMETER_NAME}:  ${paramMarkValue}/${param?.PARAMETER_MARKS}`;
          })?.join("\n\n");

          let cellContent = paramMarks ? `${paramMarks}\n\nTotal:  ${subjectMark}` : `Total:  ${subjectMark}`;
          rowArray.push(cellContent);
        });

        rowArray.push(`${item?.TotalObtainedMarks !== undefined && item?.TotalObtainedMarks !== null ? item?.TotalObtainedMarks : "-"}`);
        ws_data.push(rowArray);
      });

      // Create Worksheet
      const ws = XLSX.utils.aoa_to_sheet(ws_data);

      // Style Cells
      ws['!cols'] = [{ wch: 25 }, { wch: 15 }, { wch: 15 }]; // Student, Class, Roll No

      // Apply center alignment and text wrap to ALL cells
      for (let R = 0; R < ws_data.length; ++R) {
        for (let C = 0; C < ws_data[R].length; ++C) {
          const cellAddress = { c: C, r: R };
          const cellRef = XLSX.utils.encode_cell(cellAddress);
          if (!ws[cellRef]) continue;

          ws[cellRef].s = {
            alignment: {
              vertical: "center",
              horizontal: "center",
              wrapText: true
            }
          };

          if (R === 0) { // Header styles
            ws[cellRef].s.font = { bold: true };
          }

          if (C >= 3 && R === 0) { // Subject Columns (start at col index 3) widths
            ws['!cols'].push({ wch: 35 });
          }
        }
      }

      // Create Workbook & Save
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Exam Results");
      XLSX.writeFile(wb, `${params?.route}${props?.className ? `-${props?.className}` : ``}${props?.examName ? `-${props?.examName}` : ``}.xlsx`);

    } else if (params?.route === "accountTransaction") {
      const table: any = tableRef.current;
      const rows = table.querySelectorAll('tr');
      const ws_data: any[][] = [];

      const isTrue = props?.columns?.find(item => item?.id === "ACTION_HEADER");
      
      rows.forEach((row, rowIdx) => {
        const cols = row.querySelectorAll('td, th');
        const rowData: any[] = [];
        cols?.forEach((col, index) => {
          const hiddenSpan = col.querySelector('#hiddenLeafExcel') || "";
          if (isTrue) {
            if ((cols?.length - 1) !== index) {
              if (hiddenSpan) {
                rowData.push(formatString((hiddenSpan as any).innerText))
              } else {
                rowData.push(formatString((col as any).innerText))
              }
            }
          } else {
            if (hiddenSpan) {
              rowData.push(formatString((hiddenSpan as any).innerText))
            } else {
              rowData.push(formatString((col as any).innerText))
            }
          }
        });
        // Add Student Status column
        if (rowIdx === 0) {
          rowData.push("Student Status");
        } else {
          if (row.classList.contains('bg-yellow-300')) rowData.push("Inactive");
          else if (row.classList.contains('line-through')) rowData.push("Deleted");
          else rowData.push("Active");
        }
        ws_data.push(rowData);
      });

      const XLSX = require("xlsx-js-style");
      const ws = XLSX.utils.aoa_to_sheet(ws_data);

      rows.forEach((row, R) => {
        let color = "";
        if (R === 0) {
          for (let C = 0; C < ws_data[R].length; ++C) {
            const cellRef = XLSX.utils.encode_cell({ c: C, r: R });
            if (!ws[cellRef]) continue;
            ws[cellRef].s = { font: { bold: true } };
          }
        } else {
          let hasColor = false;
          let isStrike = false;
          if (row.classList.contains('bg-yellow-300')) { color = "FFFDE047"; hasColor = true; }
          if (row.classList.contains('line-through')) isStrike = true;

          if (hasColor || isStrike) {
            for (let C = 0; C < ws_data[R].length; ++C) {
              const cellRef = XLSX.utils.encode_cell({ c: C, r: R });
              if (!ws[cellRef]) continue;
              
              if (!ws[cellRef].s) ws[cellRef].s = {};
              if (hasColor) ws[cellRef].s.fill = { fgColor: { rgb: color } };
              if (isStrike) {
                  if (!ws[cellRef].s.font) ws[cellRef].s.font = {};
                  ws[cellRef].s.font.strike = true;
              }
            }
          }
        }
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Export");
      XLSX.writeFile(wb, filename.replace('.csv', '.xlsx'));
    } else {
      const table: any = tableRef.current;
      const rows = table.querySelectorAll('tr');
      const csv: any = [];
      const isTrue = props?.columns?.find(item => item?.id === "ACTION_HEADER");
      rows.forEach(row => {
        const cols = row.querySelectorAll('td, th');
        const rowData: any = [];
        cols?.forEach((col, index) => {
          const hiddenSpan = col.querySelector('#hiddenLeafExcel') || "";
          if (isTrue) {
            if ((cols?.length - 1) !== index) {
              if (hiddenSpan) {
                rowData.push(formatString((hiddenSpan as any).innerText))
              } else {
                rowData.push(formatString((col as any).innerText))
              }
            }
          } else {
            if (hiddenSpan) {
              rowData.push(formatString((hiddenSpan as any).innerText))
            } else {
              rowData.push(formatString((col as any).innerText))
            }
          }
        });
        csv.push(rowData.join(','));
      });
      downloadCSV(csv.join('\n'), `${params?.route}${props?.className ? `-${props?.className}` : ``}${props?.examName ? `-${props?.examName}` : ``}.csv`);
    }

    /* Export logging is temporarily disabled.
    const path = location?.pathname?.split("/") || [];
    const module = path[1] || "";
    const subModule = path[2] || "";
    const action = path[4] || "";

    const request = async () => {
      try {
        const response = await httpclient().post(
          '/data/exportLog/insert',
          {},
          {
            headers: {
              MODULE: module,
              'SUB-MODULE': subModule,
              ACTION: 'EXPORT'
            }
          }
        );
        // console.log('response', response);
      } catch (e) {
        console.error('error', e);
      }
    };

    if (module && subModule && action) {
      request();
    } else {
      console.warn('Required path segments missing:', { module, subModule, action });
    }
    */

  };

  const publishOnWebsite = async () => {
    if (!isCheckedList || isCheckedList?.length < 3 || isCheckedList?.length > 5) {
      error("Selection must include at least 3 and no more than 5 items to publish.", "error");
      return;
    }
    setLoading(true);
    try {
      const response = await httpclient().post(`data/branch/staffPublish`, { STAFF_IDS: isCheckedList?.map(({ STAFF_ID }) => STAFF_ID) });

      if ([200, 201].includes(response.status)) {
        success("Published successfully", true);
        props?.handleFeedbackCheckboxToggle();
      } else {
        console.warn("Unexpected status code:", response.status);
      }
    } catch (e) {
      console.error("Error publishing testimonials:", e);
      error("Failed to publish testimonials", true);
    } finally {
      setLoading(false);
    }
  };

  const debouncedUpdate = debounce(publishOnWebsite, 300); // 300ms delay

  return (
    <>
      <Div className={`max-w-full p-2 space-y-2 ${props.className}`}>
        <Modal
          isOpen={showModal.display}
          contentLabel="Example Modal"
          style={customStyles}
        >
          <div className="flex justify-between">
            <h1 className="font-bold text-xl">Columns</h1>
            <Close
              className="w-6 h-6 cursor-pointer"
              onClick={() => {
                setSelectedColumns([]);
                setshowModal({ display: false, ID: undefined })
              }}
            />
          </div>
          <div className="flex flex-col justify-between items-between h-[50vh] gap-4 py-2">
            <div>
              {!!formLoading ? <SetupLoader height='min-h-[4vh] max-h-[4vh]' /> : <DropDown
                notCloseMenuOnSelect={true}
                menuHeight='100px'
                minWidth={200}
                height='auto'
                customHeight='auto'
                isMulti
                getOptionValue={(option: any) => option?.id?.toUpperCase()}
                getOptionLabel={(option: any) => typeof (option?.header) === "string" ? (option?.header || option?.id) : option?.id}
                options={constantColumns /* table.getHeaderGroups()?.[0]?.headers */}
                placeholder="Select Columns"
                onChange={(column: any) => {
                  setSelectedColumns(selectedColumns => column?.map(({ id, header, accessorKey }) => (id || accessorKey || header)))
                }}
                defaultValue={reorderTable(constantColumns, selectedSavedColumns?.DISPLAY_COLUMNS)?.filter(({ id, header, accessorKey }) => selectedSavedColumns?.DISPLAY_COLUMNS?.find(item => (item === id || item === accessorKey || item === header)))}
                name="COLUMNS"
              />}
            </div>
            <div className="flex justify-center">
              <div className="flex gap-2">
                <Button
                  type="ghost"
                  label="Cancel"
                  onClick={() => {
                    setSelectedColumns([]);
                    setshowModal({ display: false, ID: undefined })
                  }}
                />
                <Button
                  type="outlined"
                  label="Reset"
                  onClick={() => {
                    handleSelectedColumnSelection(constantColumns?.map(({ id, header, accessorKey }) => (id || accessorKey || header)));
                    setshowModal({ display: false, ID: undefined });
                  }}
                />
                {<Button
                  type="primary"
                  label="Save & Apply"
                  onClick={() => {
                    handleSelectedColumnSelection()
                  }}
                />}
              </div>
            </div>
          </div>
        </Modal>

        <div className="w-full flex justify-between items-center gap-4 mb-2">
          <div className="flex-1 flex justify-start items-center">
             <div className="flex items-center gap-3">
               {!!props?.headerControlsLeft && props?.headerControlsLeft()}
             </div>
          </div>
          <div className="max-w-full flex justify-end items-center gap-4">
            {!!props?.headerControls && props?.headerControls()}
            {loading && <div>
              <SetupLoader height='h-[30px]' />
            </div>}

            {(Boolean(params?.route === "staff") && checkAdmin(authData)) && <button type={"button"} disabled={loading} className='bg-white rounded-md h-[4vh] px-4 text-sm text-black whitespace-nowrap' onClick={debouncedUpdate}>Publish on Website</button>}

          <input type="text" placeholder="Search..." value={searchInput} onChange={handleSearchChange} className="shadow p-2 rounded-lg" />
          {(props?.route && !['cumulativeReport']?.includes(props?.route) && ((['feeStudentConcession', 'student', 'examStudentResult', 'hallticket', 'report', 'feeStudentCategory'/* , 'cumulativeReport' */]?.includes(props?.route) && props?.selectedClass !== 0)) ?
            <div className='bg-gray-200 rounded-lg shadow-md w-[4.5vw] py-1 pl-3 pr-3 flex  justify-between '><span className='font-normal text-[1.15rem]'>{pageSize}</span> <span className='w-4 h-4 mr-2'> <DownArrowBlack /> </span></div> :
          
<select 
  className='shadow p-2 rounded-lg' 
  value={pageSize} // 👈 Use local state strictly here
  onChange={(e) => {
    const newSize = +e.target.value;
    
    
    setPagination({ pageIndex: 0, pageSize: newSize });

   
    if (props.dispatch) {
      props.dispatch({ page_index: 0, page_size: newSize });
    }
  }}
>
              {paginationOptions?.map((item, i) => <option key={"oqweioqhehjsehwje" + i} value={item}>
                {item}
              </option>)}
            </select>)}

            {!!props?.columnsSelectionDispatch && <button onClick={() => setshowModal({ display: true, ID: undefined })} className='border-[1px] border-solid border-primary-main bg-neutral-light py-[0.2rem] px-4 rounded-lg text-primary-main font-semibold min-h-[4vh] max-h-[4vh] text-[2vh]'>
              Columns
            </button>}
            <button disabled={!table.getRowModel().rows?.length} onClick={() => exportTableToCSV(`${params?.route || "my_table"}.csv`)} className='bg-primary-main py-2 px-4 rounded-lg text-neutral-light font-semibold'>
              Export
            </button>
          </div>
        </div>
        <ScrollableDiv
          ref={containerRef}
          style={{
            height: availableHeight,
            overflowX: 'auto'  // Changed from overflowY: 'auto'
          }}
        >
          {!!table.getRowModel().rows?.length ? (
            <div style={{ position: 'relative' }}>
              <table ref={tableRef}>
                <MemoizedTableHeader />
                <MemoizedTableBody />
              </table>
            </div>
          ) : (<NoDataFound className={"h-[80vh]"} />)}
        </ScrollableDiv>
        {!props?.notWantPages && !!table.getRowModel().rows?.length && (
          <div className="sticky w-full flex justify-between p-[0.3vw] mt-2">
            <div className="flex gap-1 w-fit whitespace-nowrap">
              <span>
                Showing{" "}
                <span className="font-semibold text-gray-900 dark">
                  {table.getState().pagination.pageIndex *
                    table.getState().pagination.pageSize +
                    1}{" "}
                </span>
                to{" "}
                <span className="font-semibold text-gray-900 dark">
                  {Math.min(
                    (table.getState().pagination.pageIndex + 1) *
                    props?.data?.length,
                  )}{" "}
                </span>
                entries out of{" "}
                <span className="font-semibold text-gray-900 dark">
                  {props?.totalEntries || props?.data?.length || 0}{" "}
                </span>
              </span>
            </div>
            <div className="w-full flex justify-end gap-2">
              <Button
                type="outlined"
                icon={<LeftArrow className="w-4 h-4" />}
                labelClasses="text-xs"
                width='w-5'
                height='h-8'
                padding='px-2 py-3'
                onClick={() => {
                  EncryptedStorage.removeItem('PAGINATION')
                  if (!!props?.dispatch) {
                    props?.dispatch({
                      page_index: table.getState().pagination.pageIndex - 1,
                      page_size: table.getState().pagination.pageSize
                    })
                    setLoading(true)
                  } else {
                    table.previousPage()
                  }
                }}
                disabled={!table.getCanPreviousPage()}
              />
              {dataQuery.pageCount < 9
                ? Array.from(Array(dataQuery.pageCount).keys()).map(
                  (item, index) => (
                    <Button
                      type={
                        table.getState().pagination.pageIndex === index
                          ? 'primary'
                          : 'outlined'
                      }
                      label={(item + 1).toString()}
                      width='w-8'
                      height='h-8'
                      padding='px-3 py-3'
                      labelClasses="text-xs flex items-center justify-center h-full"
                      onClick={() => {
                        EncryptedStorage.removeItem('PAGINATION')
                        if (!!props?.dispatch) {
                          props?.dispatch({
                            page_index: +index,
                            page_size: pageSize
                          })
                          setLoading(true)
                        } else {
                          table.setPageIndex(index)
                        }
                      }}
                    />
                  ),
                )
                : table.getState().pagination.pageIndex > 6 &&
                  table.getState().pagination.pageIndex < dataQuery.pageCount - 6
                  ? [
                    1,
                    '...',
                    table.getState().pagination.pageIndex - 1,
                    table.getState().pagination.pageIndex,
                    table.getState().pagination.pageIndex + 1,
                    table.getState().pagination.pageIndex + 2,
                    table.getState().pagination.pageIndex + 3,
                    '...',
                    dataQuery.pageCount,
                  ].map((item, index) => (
                    <Button
                      type={
                        table.getState().pagination.pageIndex + 1 === item
                          ? 'primary'
                          : 'outlined'
                      }
                      label={item.toString()}
                      labelClasses="text-xs"
                      width='w-8'
                      height='h-8'
                      padding='px-3 py-3' onClick={() => {
                        EncryptedStorage.removeItem('PAGINATION');
                        setLoading(true);
                        if (index === 0) {
                          props?.dispatch({
                            page_index: 0,
                            page_size: pageSize
                          })
                        } else if (index === 8) {
                          props?.dispatch({
                            page_index: dataQuery.pageCount - 1,
                            page_size: pageSize
                          })
                        } else {
                          props?.dispatch({
                            page_index: (item as number) - 1,
                            page_size: pageSize
                          })
                        }
                      }}
                      disabled={index === 1 || index === 7 ? true : false}
                    />
                  ))
                  : dataQuery.pageCount -
                    1 -
                    table.getState().pagination.pageIndex >
                    table.getState().pagination.pageIndex
                    ? [1, 2, 3, 4, 5, 6, 7, '...', dataQuery.pageCount].map(
                      (item, index) => (
                        <Button
                          type={
                            table.getState().pagination.pageIndex + 1 === item
                              ? 'primary'
                              : 'outlined'
                          }
                          label={item.toString()}
                          width='w-8'
                          height='h-8'
                          padding='px-3 py-3'
                          labelClasses="text-xs"
                          onClick={() => {
                            EncryptedStorage.removeItem('PAGINATION')
                            setLoading(true)
                            if (index === 8) {
                              props?.dispatch({
                                page_index: +(dataQuery.pageCount - 1),
                                page_size: pageSize
                              })
                            } else {
                              props?.dispatch({
                                page_index: +index,
                                page_size: pageSize
                              })
                            }
                          }}
                          disabled={index === 7}
                        />
                      ),
                    )
                    : [
                      1,
                      '...',
                      dataQuery.pageCount - 6,
                      dataQuery.pageCount - 5,
                      dataQuery.pageCount - 4,
                      dataQuery.pageCount - 3,
                      dataQuery.pageCount - 2,
                      dataQuery.pageCount - 1,
                      dataQuery.pageCount,
                    ].map((item, index) => (
                      <Button
                        type={
                          table.getState().pagination.pageIndex + 1 === item
                            ? 'primary'
                            : 'outlined'
                        }
                        label={item.toString()}
                        width='w-8'
                        height='h-8'
                        padding='px-3 py-3'
                        labelClasses="text-xs"
                        onClick={() => {
                          EncryptedStorage.removeItem('PAGINATION')
                          setLoading(true)
                          props?.dispatch({
                            page_index: ((+item as number) - 1),
                            page_size: pageSize
                          })
                        }}
                        disabled={index === 1}
                      />
                    ))}
              <Button
                type="outlined"
                icon={<RightArrow className="w-4 h-4" />}
                width="w-5"
                height='h-8'
                padding='px-2 py-3'
                onClick={() => {
                  if (!!props?.dispatch) {
                    EncryptedStorage.removeItem('PAGINATION')
                    props?.dispatch({
                      page_index: pageIndex + 1,
                      page_size: pageSize
                    })
                    setLoading(true)
                  } else {
                    table.nextPage()
                  }
                }}
                disabled={!table.getCanNextPage()}
              />
            </div>
          </div>
        )}
      </Div>
    </>
  );
});

const Div = styled.div`
  width: 100% !important;
  overflow: auto !important;

  table {
    box-shadow: 0px 8px 24px rgba(69, 69, 80, 0.1);
    background-color: white;
    border-collapse: collapse;
    border-spacing: 0;
    table-layout: auto;
    width: 100%;
    border-radius: 16px;
    overflow: clip;
  }
  thead {
    border-radius: 16px;
    background-color: rgba(237, 234, 254, 1);
  }
  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: rgba(237, 234, 254, 1) !important;
    padding: 0.9rem;
    color: rgba(0, 0, 0, 1);
    font-weight: 600;
    text-align: left;
    border-bottom: 1px solid #e0e3e7;
  }

  td {
    /* padding: 0.8rem 0.1rem; */
    border-bottom: 1px solid #e0e3e7;
  }

  .space {
    border-bottom: none;
    width: 0.2vw;
    /* width: 0.5vw; */
  }
`;

const ScrollableDiv = styled.div`
  overflow: auto;

  /* Default scrollbar size */
  &::-webkit-scrollbar {
    width: 2px;
    height: 2px;
    transition: width 0.3s ease, height 0.3s ease;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #1100ab;
    border-radius: 10px;
    transition: background 0.3s ease;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #0d0080;
  }

  /* Enlarge scrollbar when hovering over the container */
  &:hover::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
`;
